import * as React from 'react'
import { supabase } from './supabase'
import { AdminField, Button, Dialog } from './ui'
import { dateLabel, type OpsExtension } from './operationsModel'

type Booking = {
  id: string
  booking_reference: string
  delivery_date: string
  hire_end_date: string
  collection_date: string
  operational_delivery_date?: string | null
  operational_hire_end_date?: string | null
  operational_collection_date?: string | null
  updated_at: string
  ops_extensions?: OpsExtension[]
}

function dateError(error: unknown) {
  const text = error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error)
  const messages: Record<string, string> = {
    INVALID_BOOKING_DATES: 'Delivery must be on or before hire end, and collection must be on or after hire end.',
    EXTENSION_HIRE_END_LOCKED: 'This booking has extensions. Use Edit extension to change its hire end.',
    DELIVERY_AFTER_EXTENSION_BASE: 'Delivery must be on or before the hire end preceding the first extension.',
    UNIT_UNAVAILABLE: 'An assigned unit is unavailable for these dates. Change the units or choose different dates.',
    UNITS_MUST_MATCH_BOOKED_CLASS: 'An assigned unit is no longer active. Change the assigned units before changing dates.',
    BOOKING_CHANGED_REFRESH: 'This booking has changed. Close this form and reopen the booking before trying again.',
    BOOKING_NOT_OPEN: 'This booking is no longer open for date changes.',
    PAID_BOOKING_REQUIRED: 'Dates cannot be changed with this payment status.',
  }
  return Object.entries(messages).find(([code]) => text.includes(code))?.[1] || text
}

export function BookingDatesForm({ booking, onClose, onSaved }: {
  booking: Booking
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [form, setForm] = React.useState({
    delivery_date: booking.operational_delivery_date || booking.delivery_date,
    hire_end_date: booking.operational_hire_end_date || booking.hire_end_date,
    collection_date: booking.operational_collection_date || booking.collection_date,
  })
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const titleId = React.useId()
  const firstExtension = [...(booking.ops_extensions || [])].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))[0]
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setError('')
    if (!form.delivery_date || !form.hire_end_date || !form.collection_date || form.delivery_date > form.hire_end_date || form.collection_date < form.hire_end_date) {
      setError(dateError('INVALID_BOOKING_DATES'))
      return
    }
    if (firstExtension && form.delivery_date > firstExtension.previous_end_date) {
      setError(dateError('DELIVERY_AFTER_EXTENSION_BASE'))
      return
    }
    setBusy(true)
    try {
      const { error } = await supabase.rpc('admin_update_live_booking_dates', {
        p_booking_id: booking.id, p_delivery_date: form.delivery_date, p_hire_end_date: form.hire_end_date,
        p_collection_date: form.collection_date, p_expected_updated_at: booking.updated_at,
      })
      if (error) throw error
      await onSaved()
    } catch (error) {
      setError(dateError(error))
    } finally {
      setBusy(false)
    }
  }
  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onClose() }} purpose="form" width="min(850px, 96vw)" maxHeight="92dvh" aria-labelledby={titleId}>
    <form className="ops-dialog" onSubmit={event => void save(event)}>
      <h2 id={titleId}>Edit dates · {booking.booking_reference}</h2>
      <p>These dates update the fleet calendar and equipment availability. The original booking dates and price are kept.</p>
      <fieldset disabled={busy}>
        <div className="ops-grid">
          <AdminField>Delivery<input required type="date" max={firstExtension?.previous_end_date || form.hire_end_date} value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></AdminField>
          <AdminField>Hire end<input required type="date" disabled={!!firstExtension} min={form.delivery_date} max={form.collection_date} value={form.hire_end_date} onChange={e => setForm({ ...form, hire_end_date: e.target.value })} /></AdminField>
          <AdminField>Collection<input required type="date" min={form.hire_end_date} value={form.collection_date} onChange={e => setForm({ ...form, collection_date: e.target.value })} /></AdminField>
        </div>
        {firstExtension && <p>Use Edit extension to change the hire end. Existing extension dates stay fixed, so delivery must be on or before {dateLabel(firstExtension.previous_end_date)}.</p>}
      </fieldset>
      {error && <p className="message error" role="alert">{error}</p>}
      <div className="ops-actions"><Button type="button" disabled={busy} onClick={onClose}>Cancel</Button><Button className="primary" disabled={busy}>{busy ? 'Saving…' : 'Save dates'}</Button></div>
    </form>
  </Dialog>
}
